import { observer } from 'mobx-react';

import style from './TaskActions.module.scss';

import { FrontendTaskStatus, TaskStatus } from 'chaika-frontend-types/frontend-cms';

import { ButtonTheme } from '@components/Button';

import { TaskSection } from '../TaskSection';
import { TaskActionsRow } from './TaskActionsRow';
import { TaskActionsRowModifiers } from './TaskActionsRow/TaskActionsRow';
import { TaskAction } from './TaskAction';
import { TaskAttributes } from 'chaika-test-attributes';
import { translate } from '@chaika/i18n/helper';

import { useTaskActions } from './hooks/useTaskActions';

export const TaskActions = observer((): React.ReactElement<any> => {
    const {
        isEditMode,
        status,
        canEdit,
        canChangeStatus,
        onCreate,
        onClose,
        onUpdate,
        onDelete,
        onComplete,
        onCancelExecution,
        onToggleEditMode,
    } = useTaskActions();

    const isCreatingTask = canEdit && status === FrontendTaskStatus.Draft;
    const isEditing = canEdit && isEditMode;
    const canEditTask = canEdit && !isEditMode && status !== FrontendTaskStatus.Draft;

    return (
        <TaskSection
            divider
            className={style.stickyActions}
        >
            <TaskActionsRow showIf={isCreatingTask}>
                <TaskAction
                    theme={ButtonTheme.Primary}
                    label={translate('createTask')}
                    onClick={onCreate}
                    dataTest={TaskAttributes.Save}
                />
                <TaskAction
                    theme={ButtonTheme.Secondary}
                    label={translate('cancellation')}
                    onClick={onClose}
                    dataTest={TaskAttributes.Cancel}
                />
            </TaskActionsRow>
            <TaskActionsRow
                showIf={isEditing && !isCreatingTask}
                modifiers={[TaskActionsRowModifiers.SpaceBetween]}
            >
                <TaskAction
                    theme={ButtonTheme.Secondary}
                    label={translate('save')}
                    onClick={onUpdate}
                    dataTest={TaskAttributes.Save}
                />
                <TaskAction
                    theme={ButtonTheme.Danger}
                    label={translate('delete')}
                    onClick={onDelete}
                    dataTest={TaskAttributes.Delete}
                />
            </TaskActionsRow>
            <TaskActionsRow
                showIf={canEditTask}
                modifiers={[TaskActionsRowModifiers.SpaceBetween]}
            >
                {status === TaskStatus.Requested ? (
                    <TaskAction
                        theme={ButtonTheme.Primary}
                        label={translate('markCompleted')}
                        onClick={onComplete}
                        dataTest={TaskAttributes.Save}
                    />
                ) : (
                    <TaskAction
                        theme={ButtonTheme.Primary}
                        label={translate('markUnfulfilled')}
                        onClick={onCancelExecution}
                        dataTest={TaskAttributes.Save}
                    />
                )}
                <TaskAction
                    theme={ButtonTheme.Secondary}
                    label={translate('edit')}
                    onClick={onToggleEditMode}
                    dataTest={TaskAttributes.Save}
                />
            </TaskActionsRow>
            <TaskActionsRow showIf={!canEditTask && !isEditMode && canChangeStatus && status !== TaskStatus.Cancelled}>
                {status === TaskStatus.Requested ? (
                    <TaskAction
                        theme={ButtonTheme.Primary}
                        label={translate('markCompleted')}
                        onClick={onComplete}
                        dataTest={TaskAttributes.Save}
                    />
                ) : (
                    <TaskAction
                        theme={ButtonTheme.Secondary}
                        label={translate('returnToUnfulfilled')}
                        onClick={onCancelExecution}
                        dataTest={TaskAttributes.Save}
                    />
                )}
            </TaskActionsRow>
            <TaskActionsRow showIf={!canEditTask && !canChangeStatus && status === TaskStatus.Completed}>
                <TaskAction
                    theme={ButtonTheme.Secondary}
                    label={translate('returnToUnfulfilled')}
                    onClick={onCancelExecution}
                    dataTest={TaskAttributes.Save}
                />
            </TaskActionsRow>
        </TaskSection>
    );
});
